/**
 * 「工具」（家长端顶层页）：**孩子们共用**的东西都放这里。
 *
 * ## 为什么有这一页（2026-10-14 导航重构，用户原话）
 *
 * > "我觉得语文听写这一块，应该把数据录入等孩子们共用的部分提取出来，放在一个外部的地方，
 * >  比如一级标题栏……我觉得「共享」没必要放到那里，因为共享是针对每个孩子的进行共享的。
 * >  我觉得可以把「共享」写成「工具」，而「听写」属于一个工具，以后可能会有其他的工具，
 * >  比如英语的听写等等。"
 *
 * 于是主导航的第三格由「共享」变成「工具」（`/tools`），这一页列出可用工具。
 * **现在只有一项**：语文听写（数据录入）。将来会有英语听写等 —— 结构上直接往
 * `TOOLS` 里加一条即可，但**本轮不为未来的工具写占位假项**（假项点进去是空页，
 * 比没有这一项更糟）。
 *
 * ## 「共享」的功能去哪了（**一个都不能丢**，这是本次改动的硬约束）
 *
 * 用户说得对：共享是**针对每个孩子**的。但"共享"里混着两件不同性质的事，必须分开看：
 *
 * | 功能 | 性质 | 现在在哪 |
 * | --- | --- | --- |
 * | 待我处理的邀请（**收件箱**：同意 / 拒绝） | **顶层**：邀请是别人发给**我**的，与"当前在看哪个孩子"无关 | **本页第一段**（原样保留） |
 * | 我接受的共享（别人共享给我的孩子清单） | 顶层列表 | 本页第二段（可点进那个孩子） |
 * | 我发出的邀请 / 改权限 / 取消共享 | **某个孩子**的 | **孩子详情页**（`/students/:id`，一直在那里）；后端也不提供"我发出的邀请"列表 |
 * | 发起新邀请 | 某个孩子的（owner 才能发） | **孩子详情页**的「与另一位家长共享」卡片 |
 *
 * 也就是说：`/shares` 页上**只有**收件箱是"顶层的事"，而那一段被搬到了本页
 * —— 所以改导航没有让任何功能变得到不了。理由（为什么不是"在工具页再放一个
 * 「共享管理」条目、把 `/shares` 留着"）：那会留下**两个**都叫"共享"的顶层入口，
 * 而其中一个点进去看到的一半内容又指向孩子详情页 —— 正是用户抱怨的那种错位。
 *
 * ## 两个刚修好的行为，**不要弄回去**
 *
 * 1. 「我接受的共享」只用 `useSharedStudents()`（= `GET /students` 里 `permission !== 'owner'`
 *    的那些），**已经取消共享的人不会再出现**（`useShareMutations` 的失效链覆盖了它）。
 * 2. `pendingInvites`（"待对方同意的邀请"）的**单独展示**在孩子详情页里，本页不动它；
 *    本页只列"待我处理"的收件箱，两者不是一件事。
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StudentCard } from '../../components/parent/StudentCard';
import { IconBookOpen, IconInbox, IconShare2 } from '../../components/ui/Icons';
import { useShareMutations, useShares, useSharedStudents } from '../../lib/queries';
import { SHARE_PERMISSION_LABEL } from '../../lib/api/shares';
import { toErrorMessage } from '../../lib/api/error';
import { formatDateTimeFull } from '../../lib/utils';
import type { ShareRequest } from '../../lib/api/types';

/**
 * 工具清单。**只登记真的能用的工具**（现在只有一项）。
 *
 * 结构上是"数据驱动"的，因为用户明确说"以后可能会有其他的工具，比如英语的听写等等"：
 * 加一项 = 往这个数组里加一条 + 在 `App.tsx` 注册它的路由。
 * ⚠️ 不要为还没做的工具先放一条灰着的假项：家长点进去只会看到空页。
 */
const TOOLS: { to: string; name: string; description: ReactNode; icon: ReactNode }[] = [
  {
    to: '/tools/dictation',
    name: '语文听写',
    description: (
      <>
        课本、课文、生字与词语表的<strong>数据录入</strong>（全家共用一份），含整块粘贴批量导入。
      </>
    ),
    icon: <IconBookOpen size={24} />,
  },
];

/** 一行共享邀请：左边是谁/哪个孩子，右边是操作（同意 / 拒绝）。 */
function InviteRow({ share, children }: { share: ShareRequest; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate font-medium text-slate-900">
          {share.studentNickname}
          <span className="ml-2 text-sm font-normal text-slate-500">
            来自 {share.counterpartNickname || '某位家长'}
            {share.counterpartUsername ? `（${share.counterpartUsername}）` : ''}
          </span>
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {SHARE_PERMISSION_LABEL[share.permission]} · {formatDateTimeFull(share.createdAt)}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>
    </li>
  );
}

export function ToolsPage() {
  const sharesQuery = useShares();
  const sharedStudentsQuery = useSharedStudents();
  const { accept, reject } = useShareMutations();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const incoming = sharesQuery.data?.incoming ?? [];
  const shared = sharedStudentsQuery.data ?? [];

  const handle = async (id: string, action: () => Promise<unknown>) => {
    setBusyId(id);
    setFailed(null);
    try {
      await action();
    } catch (err) {
      // 失败**必须留下痕迹**：这里原来是 toast，现在也保留一句页内红字，
      // 因为"点了同意但没生效"如果只闪一下就消失，家长会以为已经共享成功了。
      setFailed(toErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * 主导航上的角标挂在本页（`/tools`）—— 收件箱在这里，那个数字就该指着这一格。
   * ⚠️ 家长首页的那条横幅也仍然指过来（见 `ParentHomePage`）。
   */
  const badges = incoming.length > 0 ? { '/tools': incoming.length } : undefined;

  return (
    <AppShell kind="parent" title="工具" {...(badges ? { badges } : {})}>
      <div className="space-y-6">
        {/* ---------- 工具清单 ---------- */}
        <section aria-labelledby="tools-heading" className="space-y-3">
          <h2
            id="tools-heading"
            className="text-sm font-semibold uppercase tracking-wide text-slate-500"
          >
            可用工具
          </h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {TOOLS.map((tool) => (
              <li key={tool.to}>
                <Link
                  to={tool.to}
                  title={tool.name}
                  className="card flex h-full items-start gap-3 hover:shadow-md"
                >
                  <span className="shrink-0 rounded-xl bg-brand-50 p-2 text-brand-700">
                    {tool.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900">{tool.name}</span>
                    {/*
                      说明文字对读屏**隐藏**：它是卡片的补充说明，不该被读成链接名字的一部分
                      （否则焦点落到这一条时会念出整句"课本、课文、生字与词语表的数据录入……"）。
                      链接的可访问名字由上面的 `tool.name` 与 `title` 承担。
                    */}
                    <span aria-hidden="true" className="mt-0.5 block text-xs text-slate-500">
                      {tool.description}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">
            这些工具处理的是<strong>全家人共用</strong>的内容：换个孩子进来看到的是同一份数据。
            针对某个孩子的听写设置与布置，将来会放在那个孩子的详情页里。
          </p>
        </section>

        {/* ---------- 收件箱：别人把孩子共享给我 ---------- */}
        <Card>
          <CardHeader
            title="共享给我的"
            description="共享是「针对某个孩子」的事：别人把孩子共享给你，你在这里同意；把一个孩子共享给别人，在那个孩子的详情页里操作。"
          />

          <section aria-labelledby="incoming-heading" className="space-y-3">
            <div className="flex items-center gap-2">
              <IconInbox size={18} />
              <h3
                id="incoming-heading"
                className="text-sm font-semibold uppercase tracking-wide text-slate-500"
              >
                待我处理
              </h3>
              {incoming.length > 0 && <Badge tone="danger">{incoming.length}</Badge>}
            </div>

            {sharesQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : incoming.length === 0 ? (
              <EmptyState
                icon="📨"
                title="没有待处理的邀请"
                description="其他家长把孩子共享给你时，邀请会出现在这里。"
              />
            ) : (
              <ul className="space-y-2">
                {incoming.map((share) => (
                  <InviteRow key={share.id} share={share}>
                    <span className="text-xs text-slate-500">
                      对方给的权限：{SHARE_PERMISSION_LABEL[share.permission]}
                    </span>
                    <Button
                      size="sm"
                      loading={busyId === share.id}
                      onClick={() => void handle(share.id, () => accept.mutateAsync(share.id))}
                    >
                      同意
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === share.id}
                      onClick={() => void handle(share.id, () => reject.mutateAsync(share.id))}
                    >
                      拒绝
                    </Button>
                  </InviteRow>
                ))}
              </ul>
            )}

            {failed !== null && (
              <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                操作失败：{failed}
              </p>
            )}
          </section>

          {/* ---------- 我接受的共享 ---------- */}
          <section aria-labelledby="accepted-heading" className="mt-6 space-y-3">
            <div className="flex items-center gap-2">
              <IconShare2 size={18} />
              <h3
                id="accepted-heading"
                className="text-sm font-semibold uppercase tracking-wide text-slate-500"
              >
                我接受的共享
              </h3>
              {shared.length > 0 && <Badge>{shared.length}</Badge>}
            </div>

            {sharedStudentsQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : shared.length === 0 ? (
              <p className="text-sm text-slate-500">
                还没有别人把孩子共享给你。要邀请另一位家长一起管孩子，请在
                <strong>孩子详情页</strong>的「与另一位家长共享」里发起。
              </p>
            ) : (
              <ul className="space-y-3">
                {shared.map((student) => (
                  <li key={student.id}>
                    <StudentCard student={student} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </Card>

        <Card>
          <CardHeader
            title="共享是怎么运作的"
            description="把孩子共享给另一位家长后，对方可以查看进度；选择「可编辑」还能新建目标和打卡任务。"
          />
          <ul className="list-inside list-disc space-y-1 text-sm text-slate-500">
            <li>只有孩子的创建者（owner）可以发起共享或调整权限。</li>
            <li>被共享的家长不能再把同一个孩子转共享给第三个人。</li>
            <li>「仅查看」的家长只能看，不能新建或修改目标。</li>
            <li>
              <strong>打卡与兑换只有孩子本人能做</strong> —— 家长（包括「可编辑」）可以查看，
              但不能替孩子打卡、完成或兑换；孩子自己的记录由他自己产生。
            </li>
            <li>
              要取消共享、改权限或看"待对方同意的邀请"，请进
              <strong>那个孩子的详情页</strong> —— 共享天然是"某个孩子"的事。
            </li>
          </ul>
        </Card>
      </div>
    </AppShell>
  );
}
